if (!customElements.get("newsletter-signup")) {
  customElements.define(
    "newsletter-signup",
    class extends HTMLElement {
      connectedCallback() {
        if (this.form) {
          document.addEventListener("submit", this.onSubmit);
          return;
        }
        this.form = this.querySelector("form");
        if (!this.form) return;
        this.error = this.querySelector("[data-newsletter-error]");
        this.success = this.querySelector("[data-newsletter-success]");
        this.fields = this.querySelector("[data-newsletter-fields]");
        this.progress = this.querySelector("[data-newsletter-progress]");
        this.button = this.form.querySelector('button[type="submit"]');
        this.email = this.form.querySelector('[name="contact[email]"]');

        // Install before Shopify wires CAPTCHA on interaction / DOMContentLoaded.
        // Shopify wraps form.submit, obtains its token, then calls this transport.
        // Never call the underlying CAPTCHA library or skip its submit listeners.
        this.form.submit = () => this.send();
        this.form.addEventListener(
          "submit",
          (event) => {
            if (this.pending) {
              event.preventDefault();
              event.stopImmediatePropagation();
              return;
            }
            this.error.hidden = true;
            this.error.textContent = "";
            this.email.removeAttribute("aria-invalid");
            this.email.removeAttribute("aria-describedby");
            this.setPending(true);
            this.timeout = setTimeout(() => {
              this.controller?.abort();
              this.showError("We couldn’t confirm your signup. Please try again.");
            }, 45000);
          },
          true,
        );
        // Run after Shopify's document listener. A prevented event means Shopify
        // is handling verification and will call form.submit when it is ready.
        this.onSubmit = (event) => {
          if (event.target !== this.form || event.defaultPrevented) return;
          event.preventDefault();
          this.send();
        };
        document.addEventListener("submit", this.onSubmit);
      }

      disconnectedCallback() {
        clearTimeout(this.timeout);
        this.controller?.abort();
        document.removeEventListener("submit", this.onSubmit);
      }

      setPending(pending) {
        this.pending = pending;
        this.button.disabled = pending;
        this.progress.hidden = !pending;
        this.form.setAttribute("aria-busy", String(pending));
        if (!pending) clearTimeout(this.timeout);
      }

      showError(message) {
        this.setPending(false);
        this.error.textContent = message;
        this.error.hidden = false;
        this.email.setAttribute("aria-describedby", this.error.id);
      }

      async send() {
        if (this.sending || !this.pending) return;
        this.sending = true;
        this.controller = new AbortController();
        try {
          const response = await fetch(this.form.action, {
            method: "POST",
            body: new URLSearchParams(new FormData(this.form)),
            credentials: "same-origin",
            signal: this.controller.signal,
          });
          const location = new URL(response.url);
          // An interactive Shopify challenge requires its own page, never a success message.
          if (location.pathname.replace(/\/$/, "").endsWith("/challenge")) {
            this.showError("Please complete Shopify’s verification to finish signing up. ");
            const link = document.createElement("a");
            link.href = location.href;
            link.textContent = "Complete verification";
            this.error.append(link);
            return;
          }
          if (!response.ok) throw new Error("Signup request failed");
          const html = new DOMParser().parseFromString(await response.text(), "text/html");
          const returnedForm = html.getElementById(this.form.id);
          const returnedError = returnedForm?.querySelector("[data-newsletter-error]:not([hidden])");
          if (returnedError) {
            this.showError(returnedError.textContent.trim());
            this.email.setAttribute("aria-invalid", "true");
            return;
          }
          // Require Shopify's rendered success state, not just HTTP 200.
          if (!returnedForm?.querySelector('[data-newsletter-success][data-accepted="true"]')) {
            throw new Error("Signup not confirmed");
          }
          this.setPending(false);
          this.error.hidden = true;
          this.fields.hidden = true;
          this.success.hidden = false;
          this.success.focus({ preventScroll: true });
        } catch {
          this.showError("We couldn’t confirm your signup. Please try again.");
        } finally {
          this.sending = false;
        }
      }
    },
  );
}
